import { frontendURL } from '../../../helper/URLHelper';
import store from '../../../store/index';
import {
  resolveSubscriptionAccess,
  hasHighTierAccess,
} from '../../../helper/subscriptionHelper';
import { resolveModuleName } from '../../../helper/moduleGuards';
import { TABS } from './constants';
import TicketsPage from './TicketsPage.vue';
import TicketDetailPage from './TicketDetailPage.vue';
import TicketDefaultsPage from './TicketDefaultsPage.vue';
import CondosPage from './CondosPage.vue';
import CondoDetailsPage from './CondoDetailsPage.vue';
import ResidentsPage from './ResidentsPage.vue';
import CategoriesPage from './CategoriesPage.vue';
import RolesPage from './RolesPage.vue';
import TeamPage from './TeamPage.vue';
import RoutingPage from './RoutingPage.vue';
import SlaPage from './SlaPage.vue';
import CalendarPage from './CalendarPage.vue';
import ReportsPage from './ReportsPage.vue';

// Module restriction guard for the Co-living module. Same chain as the Real
// Estate guard: active subscription, then pro/enterprise tier, then this
// module specifically active. Modules are mutually exclusive per account, so
// this also keeps the other modules' routes out of reach.
const moduleAccessGuard = async (to, from, next) => {
  if (process.env.CYPRESS_TEST_BUILD === 'true' && window.Cypress) {
    return next();
  }

  const accountId = to.params.accountId;

  const { hasActiveSubscription, hasError } = await resolveSubscriptionAccess(
    store,
    accountId
  );
  if (hasError) {
    return next({
      name: 'subscription_error',
      params: { accountId },
      query: { redirect: to.fullPath },
    });
  }
  if (!hasActiveSubscription) {
    return next({ name: 'pricing_table', params: { accountId } });
  }

  const moduleName = await resolveModuleName(store, accountId);
  if (!hasHighTierAccess(store) || moduleName !== 'co_living') {
    return next({ name: 'dashboard', params: { accountId } });
  }

  return next();
};

const adminOnly = { permissions: ['administrator'] };
// Agents may read the ticket list and a ticket (read-only): the server gives
// them no allowed steps, and every other portal route refuses them.
const adminAndAgent = { permissions: ['administrator', 'agent'] };

export default {
  routes: [
    {
      // The Co-living page opens on its first tab.
      path: frontendURL('accounts/:accountId/co_living'),
      name: 'co_living',
      redirect: to => ({
        name: TABS[0].route,
        params: { accountId: to.params.accountId },
      }),
      meta: adminOnly,
      beforeEnter: moduleAccessGuard,
    },
    {
      path: frontendURL('accounts/:accountId/co_living_tickets'),
      name: 'co_living_tickets',
      component: TicketsPage,
      meta: adminAndAgent,
      beforeEnter: moduleAccessGuard,
    },
    {
      // Before the :ticketId route so "defaults" is not read as a ticket id.
      path: frontendURL('accounts/:accountId/co_living_tickets/defaults'),
      name: 'co_living_ticket_defaults',
      component: TicketDefaultsPage,
      meta: adminOnly,
      beforeEnter: moduleAccessGuard,
    },
    {
      path: frontendURL('accounts/:accountId/co_living_tickets/:ticketId'),
      name: 'co_living_ticket',
      component: TicketDetailPage,
      meta: adminAndAgent,
      beforeEnter: moduleAccessGuard,
    },
    {
      path: frontendURL('accounts/:accountId/co_living/condos'),
      name: 'co_living_condos',
      component: CondosPage,
      meta: adminOnly,
      beforeEnter: moduleAccessGuard,
    },
    {
      path: frontendURL('accounts/:accountId/co_living/condos/:condoId'),
      name: 'co_living_condo',
      component: CondoDetailsPage,
      meta: adminOnly,
      beforeEnter: moduleAccessGuard,
    },
    {
      path: frontendURL('accounts/:accountId/co_living/residents'),
      name: 'co_living_residents',
      component: ResidentsPage,
      meta: adminOnly,
      beforeEnter: moduleAccessGuard,
    },
    {
      path: frontendURL('accounts/:accountId/co_living/categories'),
      name: 'co_living_categories',
      component: CategoriesPage,
      meta: adminOnly,
      beforeEnter: moduleAccessGuard,
    },
    {
      path: frontendURL('accounts/:accountId/co_living/roles'),
      name: 'co_living_roles',
      component: RolesPage,
      meta: adminOnly,
      beforeEnter: moduleAccessGuard,
    },
    {
      path: frontendURL('accounts/:accountId/co_living/team'),
      name: 'co_living_team',
      component: TeamPage,
      meta: adminOnly,
      beforeEnter: moduleAccessGuard,
    },
    {
      path: frontendURL('accounts/:accountId/co_living/routing'),
      name: 'co_living_routing',
      component: RoutingPage,
      meta: adminOnly,
      beforeEnter: moduleAccessGuard,
    },
    {
      path: frontendURL('accounts/:accountId/co_living/sla'),
      name: 'co_living_sla',
      component: SlaPage,
      meta: adminOnly,
      beforeEnter: moduleAccessGuard,
    },
    {
      path: frontendURL('accounts/:accountId/co_living/calendar'),
      name: 'co_living_calendar',
      component: CalendarPage,
      meta: adminOnly,
      beforeEnter: moduleAccessGuard,
    },
    {
      path: frontendURL('accounts/:accountId/co_living/reports'),
      name: 'co_living_reports',
      component: ReportsPage,
      meta: adminOnly,
      beforeEnter: moduleAccessGuard,
    },
  ],
};
